import { EventMeta } from '../../../interfaces/meta.interface';

type DistributionGroupMemberDescFields = {
    group: string;
    member: string;
};

const AddDistributionGroupMember: EventMeta<DistributionGroupMemberDescFields> = {
    title: 'Distribution Group Member Added',
    default: {
        description: 'Distribution Group Member Added',
        longDescription: 'A single recipient was added to distribution group and mail-enabled security group.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => ({
        group: log.group,
        member: log.member,
    }),
    classifications: [
        {
            severity: 'critical',
            description: (fields) =>
                `A suspicious IP attempted to add a member to a distribution or mail-enabled security group`,
            longDescription: (fields) =>
                `A recipient was added to a distribution group or mail-enabled security group from a suspicious IP address that is not in your allowed list. This activity could indicate an attempt to manipulate group membership or gain unauthorized access to communications.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'danger',
            description: (fields) =>
                `An unapproved IP attempted to add a member to a distribution or mail-enabled security group`,
            longDescription: (fields) =>
                `A recipient was added to a distribution group or mail-enabled security group from an IP address that is not in your allowed list. This activity may indicate an attempt to modify group membership or gain unauthorized access to communications.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP added a recipient to a group`,
            longDescription: (fields) =>
                `A recipient was added to a distribution group or mail-enabled security group. The action originated from a suspicious IP. While the addition itself may be a normal administrative activity, the source IP being flagged as suspicious makes it noteworthy. This event is informational and does not require immediate action, but monitoring for similar group changes from suspicious IPs may be beneficial.`,
            predicates: {
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'info',
            description: (fields) =>
                `A Microsoft system operation (synchronization server IP) added a recipient to a group`,
            longDescription: (fields) =>
                `A recipient was added to a distribution group or mail-enabled security group. This was logged as a Microsoft system operation event, with the IP address being the Microsoft server that handled the synchronization. This entry is informational only and reflects standard background activity. No action is required unless unexpected changes to group membership are observed.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        {
            severity: 'info',
            description: (fields) => `A recipient was added to a distribution or mail-enabled security group`,
            longDescription: (fields) =>
                `A recipient was added to a distribution group or mail-enabled security group. This is a standard informational record of group membership changes. No unusual conditions were flagged with this event. It does not require action unless the change to group membership was unexpected.`,
        },
    ],
};

export default AddDistributionGroupMember;
